import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { buildSignatureUrl, getSignatureTokenStatus, SignatureTokenStatus } from '../../services/signatureTokenService';
import './QrSignatureModal.css';

interface QrSignatureModalProps {
  token: string;
  expiresAt: string;
  onClose: () => void;
  onSigned: () => void;
}

const POLL_INTERVAL_MS = 3000;

const QrSignatureModal: React.FC<QrSignatureModalProps> = ({ token, expiresAt, onClose, onSigned }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<SignatureTokenStatus>('PENDING');
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const signatureUrl = buildSignatureUrl(token);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      try {
        const result = await getSignatureTokenStatus(token);
        setStatus(result.status);
        if (result.status === 'USED') {
          stopPolling();
          setTimeout(() => onSigned(), 2000);
        } else if (result.status === 'EXPIRED' || result.status === 'INVALID') {
          stopPolling();
        }
      } catch {
        // silently ignore transient network errors
      }
    }, POLL_INTERVAL_MS);

    return stopPolling;
  }, [token, stopPolling, onSigned]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(signatureUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select the text for manual copy
    }
  }, [signatureUrl]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const expiresDate = new Date(expiresAt);
  const expiresFormatted = expiresDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="qr-modal-overlay" onClick={handleOverlayClick}>
      <div className="qr-modal-content">
        <div className="qr-modal-header">
          <h3>{t('qrSignature.title')}</h3>
          <button className="qr-modal-close" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="qr-modal-body">
          {status === 'PENDING' && (
            <>
              <p className="qr-modal-instructions">{t('qrSignature.instructions')}</p>
              <div className="qr-code-container">
                <QRCodeSVG value={signatureUrl} size={220} level="M" />
              </div>
              <p className="qr-expiry">
                {t('qrSignature.expiresAt', { time: expiresFormatted })}
              </p>
              <div className="qr-url-row">
                <span className="qr-url-text" title={signatureUrl}>
                  {signatureUrl}
                </span>
                <button className="btn-copy" onClick={handleCopy} title={t('qrSignature.copyLink')}>
                  <span className="material-icons">{copied ? 'check' : 'content_copy'}</span>
                </button>
              </div>
              <p className="qr-waiting">
                <span className="material-icons spin">sync</span>
                {t('qrSignature.waitingForSignature')}
              </p>
            </>
          )}

          {status === 'USED' && (
            <div className="qr-status-message qr-status-success">
              <span className="material-icons">check_circle</span>
              <p>{t('qrSignature.signatureReceived')}</p>
            </div>
          )}

          {(status === 'EXPIRED' || status === 'INVALID') && (
            <div className="qr-status-message qr-status-error">
              <span className="material-icons">error_outline</span>
              <p>{t('qrSignature.tokenExpired')}</p>
            </div>
          )}
        </div>

        <div className="qr-modal-footer">
          <button onClick={onClose} className="btn-cancel">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QrSignatureModal;
